<template>
    <Transition name="byline">
        <div v-if="lastChangeInfo" class="last-changed-info">
            <Avatar
                :image="userFromStore?.profile_picture ?? undefined"
                :label="
                    userFromStore?.profile_picture
                        ? undefined
                        : getInitials(lastChangeInfo.user_name)
                "
                shape="square"
                size="normal"
                class="user-avatar"
                :class="{ 'user-avatar--pulsing': pulseDuration }"
                :style="
                    pulseDuration ? { '--pulse-duration': pulseDuration } : {}
                "
            />
            <span class="username">{{ lastChangeInfo.user_name }}</span>
            <span class="separator">·</span>
            <span class="timestamp">changed {{ formattedDate }}</span>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import Avatar from "primevue/avatar";
import { computed, ref, watch } from "vue";

import { useServices } from "@/lib/openapi";
import { HistoryEntry } from "@/lib/openapi/gen/types.gen";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useUsersManager, useUsersStore } from "@/stores/users-store";

const { isFetching } = useCurrentEntityManager();

const props = defineProps<{
    entityId: string;
    entityType: EntityType;
    attribute?: string;
}>();

interface LastChangeInfo {
    user_name: string;
    photo_url?: string | null;
    timestamp: number;
}

// Module-level cache to track which entities don't have history to avoid repeated 404s
// This persists across component instances
const entitiesWithoutHistory = new Set<string>();

const lastChangeInfo = ref<LastChangeInfo | null>(null);
const { users } = useUsersManager();
const usersStore = useUsersStore();

const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: false,
    },
});

// Find the user in the users store by name
const userFromStore = computed(() => {
    if (!lastChangeInfo.value) return null;
    return users.value.find(
        (user) => user.name === lastChangeInfo.value!.user_name
    );
});

const formattedDate = computed(() => {
    if (!lastChangeInfo.value) return "";

    const date = new Date(lastChangeInfo.value.timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60_000);
    const diffHours = Math.floor(diffMs / 3_600_000);
    const diffDays = Math.floor(diffMs / 86_400_000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
        });
    }

    return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
});

// Pulse speed reflects recency: fast = within a day, slower = within a week/month, none = older.
const pulseDuration = computed(() => {
    if (!lastChangeInfo.value) return null;
    const diffDays = (Date.now() - lastChangeInfo.value.timestamp) / 86_400_000;
    if (diffDays < 1) return "0.8s";
    if (diffDays < 7) return "2s";
    if (diffDays < 30) return "4s";
    return null;
});

const getInitials = (name: string) => {
    return name
        .split(" ")
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase()
        .substring(0, 2);
};

const fetchLastChangeInfo = async () => {
    if (!props.entityId || !props.entityType) return;

    // Check cache - if we know this entity doesn't have history, don't make the request
    // This prevents repeated 404 errors in the console
    const attribute = props.attribute || "description";
    const cacheKey = `${props.entityType}:${props.entityId}:${attribute}`;
    if (entitiesWithoutHistory.has(cacheKey)) {
        return;
    }

    // Load users to ensure we have profile pictures available
    await usersStore.getAllUsers();

    try {
        const { data, response } =
            await api.startBizEntitiesEntityTypeEntityIdAttributeHistoryGet({
                throwOnError: false, // Don't throw on 404 - it's expected for entities without history
                path: {
                    entity_type: props.entityType,
                    entity_id: props.entityId,
                    attribute,
                },
                query: {
                    limit: 1, // Get only the most recent change
                },
            });

        // Silently handle 404 errors - it's normal for entities without history
        if (response.status === 404) {
            // Cache this entity as not having history to avoid future requests
            entitiesWithoutHistory.add(cacheKey);
            return;
        }

        if (response.ok && data) {
            // Extract the most recent history entry
            const historyEntries = data.history as HistoryEntry[];

            if (historyEntries && historyEntries.length > 0) {
                const latestEntry = historyEntries[0];

                lastChangeInfo.value = {
                    user_name: latestEntry.author.name,
                    photo_url: null, // API doesn't provide this
                    timestamp: new Date(latestEntry.author.date).getTime(),
                };
            }
        }
    } catch (error: unknown) {
        // Silently handle 404 errors - it's normal for entities without history
        // Check if error has response with 404 status
        const errorObj = error as {
            response?: { status?: number; url?: string };
            detail?: unknown[];
            status?: number;
            statusText?: string;
        };

        // Check multiple ways the error might indicate a 404
        const is404 =
            errorObj?.response?.status === 404 ||
            errorObj?.status === 404 ||
            (errorObj?.response?.url?.includes("/biz/entities/") &&
                errorObj?.response?.url?.includes("/history") &&
                errorObj?.response?.status === 404);

        // Also check if the error detail indicates NOT_FOUND
        const isNotFound =
            errorObj?.detail &&
            Array.isArray(errorObj.detail) &&
            errorObj.detail.length > 0 &&
            typeof errorObj.detail[0] === "object" &&
            errorObj.detail[0] !== null &&
            "type" in errorObj.detail[0] &&
            errorObj.detail[0].type === "NOT_FOUND";

        if (is404 || isNotFound) {
            // Entity or attribute doesn't have history yet - this is expected
            // Cache this entity to avoid future requests
            const attribute = props.attribute || "description";
            const cacheKey = `${props.entityType}:${props.entityId}:${attribute}`;
            entitiesWithoutHistory.add(cacheKey);
            // Silently return without logging
            return;
        }

        // Only log unexpected errors (not 404s)
        if (errorObj?.response?.status !== 404 && errorObj?.status !== 404) {
            console.error("Error fetching entity history:", error);
        }
    }
};

watch(
    isFetching,
    async () => {
        if (!isFetching.value) {
            await fetchLastChangeInfo();
        }
    },
    { immediate: true }
);
</script>

<style scoped lang="scss">
.last-changed-info {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
}

.user-avatar {
    --p-avatar-width: 2rem;
    --p-avatar-height: 2rem;
    --p-avatar-font-size: 0.75rem;
    --p-avatar-color: var(--text-color-primary-contrast);
    --p-avatar-border-radius: 0;

    &:not(.p-avatar-image) {
        --p-avatar-background: var(--stekz-primary-color);
    }

    outline: 1.5px solid
        color-mix(in srgb, var(--stekz-primary-color) 40%, transparent);
    outline-offset: 1px;
    flex-shrink: 0;
    object-fit: cover;

    &--pulsing {
        animation: pulse-ring var(--pulse-duration, 2s) ease-in-out infinite;
    }
}

@keyframes pulse-ring {
    0%,
    100% {
        box-shadow: 0 0 0 0
            color-mix(in srgb, var(--stekz-primary-color) 0%, transparent);
    }
    50% {
        box-shadow: 0 0 0 4px
            color-mix(in srgb, var(--stekz-primary-color) 25%, transparent);
    }
}

.username {
    font-size: 0.85rem;
    font-weight: 600;
}

.separator {
    color: var(--text-color-secondary);
    font-size: 0.85rem;
}

.timestamp {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
}

// Fade in + drift up on load
.byline-enter-active {
    transition:
        opacity 350ms ease-out,
        transform 350ms ease-out;
}
.byline-enter-from {
    opacity: 0;
    transform: translateY(6px);
}
</style>
